type GraphQLErrorCandidate = {
  message?: unknown;
  extensions?: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isCandidate = (value: unknown): value is GraphQLErrorCandidate => isRecord(value);

/**
 * Кандидаты на доменную GraphQL-ошибку: элементы `ApolloError.graphQLErrors`
 * либо сама ошибка, если у неё есть `extensions` (не-Apollo обёртки).
 * Работает с `unknown`, чтобы вызывающим не нужны были небезопасные касты ApolloError.
 */
export const getGraphQLErrorCandidates = (error: unknown): GraphQLErrorCandidate[] => {
  if (!isRecord(error)) {
    return [];
  }

  if (Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors.filter(isCandidate);
  }

  if (isRecord(error.extensions)) {
    return [error];
  }

  return [];
};

/**
 * Первое сообщение Laravel-валидации из `extensions.validation` GraphQL-ошибки.
 * `field` ограничивает поиск полем (точное имя или суффикс `*.field`); без него берётся первое поле.
 */
export const getGraphQLValidationMessage = (error: unknown, field?: string): string | null => {
  for (const candidate of getGraphQLErrorCandidates(error)) {
    const validation = candidate.extensions?.validation;

    if (!isRecord(validation) || Array.isArray(validation)) {
      continue;
    }

    for (const [key, value] of Object.entries(validation)) {
      if (field !== undefined && key !== field && !key.endsWith(`.${field}`)) {
        continue;
      }

      const message = Array.isArray(value) ? value.find((item): item is string => typeof item === 'string') : value;

      if (typeof message === 'string') {
        return message;
      }
    }
  }

  return null;
};

/** Первый машинный код `extensions.code` из GraphQL-ошибки; null, если кода нет. */
export const getGraphQLErrorCode = (error: unknown): string | null => {
  for (const candidate of getGraphQLErrorCandidates(error)) {
    const code = candidate.extensions?.code;

    if (typeof code === 'string') {
      return code;
    }
  }

  return null;
};

/**
 * Текст ошибки для оператора: сообщение валидации или клиентобезопасный текст доменной
 * ошибки (у неё есть `extensions.code`), иначе `fallback`.
 */
export const getGraphQLDomainMessage = (error: unknown, fallback: string): string => {
  const validation = getGraphQLValidationMessage(error);

  if (validation !== null) {
    return validation;
  }

  const domain = getGraphQLErrorCandidates(error).find(
    (candidate) => typeof candidate.extensions?.code === 'string' && typeof candidate.message === 'string',
  );

  return typeof domain?.message === 'string' ? domain.message : fallback;
};
